import React, { useCallback, useEffect, useState } from "react";
import { getTimeZoneLabel } from "../../../shared/utils/timeZone";
import { btn } from "../../../shared/styles/buttonStyles";
import axios from "axios";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { useMsal } from "@azure/msal-react";
import { useLoading } from "../../../shared/state/LoadingContext";
import { toast } from "react-toastify";
import formatTimeTo12Hour from "../../../shared/utils/formatTimeTo12Hour";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import { PaginatedResponse } from "../../../shared/types/pagination";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  formatActivityValue,
  hasActivityValueChanged,
  isExecutionActive,
  mapExecutionFeedEntries,
  parseActivityDetailsJson,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import { getStatusTextClass } from "../../../shared/utils/statusText";
import { getAccessToken } from "../../../shared/auth/getAccessToken";
import formatTimeTo24Hour from "../../../shared/utils/formatTimeTo24Hour";

// ── Type Definitions: Data structures for jobs, activity logs, and executions ──

interface JobRow {
  rowKey: string;
  serverId: string;
  server: string;
  lastRun?: string;
  scheduleTimeValue: string;
  isEnabled: boolean;
}

interface ActivityLogEntry {
  id: string;
  actionType: string;
  status: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  durationMs?: number | null;
  changesJson?: string;
  errorMessage?: string;
}

interface ExecutionEntry {
  id: string;
  executionId: string;
  actionType: string;
  executionType: string;
  triggerType: string;
  status: string;
  statusMessage?: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  runningAtUtc?: string | null;
  updatedAtUtc?: string;
  durationMs?: number | null;
  errorMessage?: string;
  metadataJson?: string;
}

// Keep the target visible even when a schedule value did not change
const withServerLines = (parsed: any, lines: string[]) => {
  const serverLines: string[] = [];
  if (parsed?.server) {
    serverLines.push(`Server: ${formatActivityValue(parsed.server)}`);
  }
  if (parsed?.serverId) {
    serverLines.push(`Server ID: ${formatActivityValue(parsed.serverId)}`);
  }

  return [...serverLines, ...lines];
};

// ── Main Component: DbBackupSchedule ────────────────────────────────────────────

const DbBackupSchedule = () => {
  // ── State Management & Authentication
  const { instance, accounts } = useMsal();
  const { setLoading } = useLoading();

  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const apiUrl = `${process.env.REACT_APP_API_URL}/api/xuno`;


  // ── API Functions: Data fetching for jobs and activity logs ────────────────
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      const response = await axios.get(`${apiUrl}/jobs`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const rows = Array.isArray(response.data) ? response.data : [];
      setJobs(rows.map((row: any) => ({
        ...row,
        scheduleTimeValue: formatTimeTo24Hour(row.scheduleTime, "00:00"),
      })));
    } catch {
      toast.error("Failed to load DB backup schedules.");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, setLoading]);

  // Xuno audit ChangesJson is { key: { before, after } }, not Prestia's fields array
  const extractAuditChangeLines = (changesJson?: string) => {
    const parsed = parseActivityDetailsJson<any>(changesJson);
    const labels: Record<string, string> = {
      scheduleTime: "Schedule Time",
      isEnabled: "Schedule Enabled",
    };
    const lines = Object.entries(labels)
      .filter(([key]) => parsed?.[key] && hasActivityValueChanged(parsed[key].before, parsed[key].after))
      .map(([key, label]) => `${label}: ${formatActivityValue(parsed[key].before)} -> ${formatActivityValue(parsed[key].after)}`);

    return withServerLines(parsed, lines);
  };

  const getActivityChangeLines = (log: ActivityFeedEntry) => {
    if (log.source === "execution") {
      const lines: string[] = [];

      const parsed = parseActivityDetailsJson<any>(log.detailsJson);
      if (parsed?.totalDatabases !== undefined) {
        lines.push(`Databases Processed: ${formatActivityValue(parsed.completedDatabases)} / ${formatActivityValue(parsed.totalDatabases)}`);
      }

      if (parsed?.failedDatabases !== undefined && Number(parsed.failedDatabases) > 0) {
        lines.push(`Failed Databases: ${formatActivityValue(parsed.failedDatabases)}`);
      }

      return withServerLines(parsed, lines).slice(0, 12);
    }

    return extractAuditChangeLines(log.detailsJson).slice(0, 12);
  };

  const buildActivityFeed = (executionItems: ExecutionEntry[], auditItems: ActivityLogEntry[]): ActivityFeedEntry[] => {
    const executionFeed = mapExecutionFeedEntries(executionItems);

    const auditFeed: ActivityFeedEntry[] = auditItems
      .filter((log) => log.actionType === "save_schedule")
      .map((log) => ({
        id: log.id,
        source: "audit" as const,
        actionType: log.actionType,
        status: log.status,
        actorName: log.actorName,
        actorEmail: log.actorEmail,
        startedAtUtc: log.startedAtUtc,
        updatedAtUtc: undefined,
        durationMs: log.durationMs,
        detailsJson: log.changesJson,
        errorMessage: log.errorMessage,
      }));

    return sortActivityFeedEntries([...executionFeed, ...auditFeed]);
  };

  const fetchActivityLogs = useCallback(async (page = 1, showErrorToast = true, showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setActivityLogsLoading(true);
      }
      const accessToken = await getAccessToken();
      const [auditItems, executionItems] = await Promise.all([
        axios.get<PaginatedResponse<ActivityLogEntry>>(`${apiUrl}/jobs/activity-logs`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
        axios.get<PaginatedResponse<ExecutionEntry>>(`${apiUrl}/jobs/executions`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
      ]);
      const mergedFeed = buildActivityFeed(executionItems, auditItems);
      const totalCount = mergedFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = mergedFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      setHasActiveExecutions(executionItems.some((item) => isExecutionActive(item.status)));
    } catch {
      // Failed poll keeps the last rows and keeps polling
      if (!showLoadingState) {
        return;
      }
      if (showErrorToast) {
        toast.error("Failed to load activity logs.");
      }
      setActivityFeedEntries([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
    } finally {
      if (showLoadingState) {
        setActivityLogsLoading(false);
      }
    }
  }, [apiUrl]);

  const updateJob = async (jobId: string, scheduleTime: string, isEnabled: boolean) => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      await axios.put(
        `${apiUrl}/updatejobs/${jobId}`,
        {
          scheduleTime: formatTimeTo12Hour(scheduleTime),
          isEnabled,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      await fetchJobs();
      void fetchActivityLogs(1, false, false);
      const serverName = jobs.find((job) => job.rowKey === jobId)?.server || jobId;
      toast.success(`Schedule applied for ${serverName}.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to apply DB backup schedule."));
    } finally {
      setLoading(false);
    }
  };

  const runBackupNow = async (jobId: string) => {
    try {
      setLoading(true);
      const accessToken = await getAccessToken();
      await axios.post(
        `${apiUrl}/jobs/${jobId}/run`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      setHasActiveExecutions(true);
      await fetchJobs();
      void fetchActivityLogs(1, false, false);
      const serverName = jobs.find((job) => job.rowKey === jobId)?.server || jobId;
      toast.success(`Backup queued for ${serverName}.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to run DB backup now."));
    } finally {
      setLoading(false);
    }
  };

  // ── Effects: Initialize data on mount and handle polling ──────────────────
  useEffect(() => {
    if (!instance || accounts.length === 0) {
      return;
    }

    void fetchJobs();
    void fetchActivityLogs(1);
  }, [accounts, fetchActivityLogs, fetchJobs, instance]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchActivityLogs(activityLogsPage, false, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activityLogsPage, fetchActivityLogs, hasActiveExecutions]);

  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText: log.source === "execution"
      ? (log.triggerType === "manual_action" ? "Run Backup Now" : "Run Backup")
      : "Apply Schedule",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: getActivityChangeLines(log),
    errorMessage: log.errorMessage,
    pipelineRunUrl: undefined,
  }));

  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Sets scheduled and manual backups for Xuno MySQL flexible servers.">
          <h3 className="text-sm font-semibold text-slate-800">Backup storage</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Set a backup time for each MySQL server.</li>
            <li>Backups are stored in <span className="font-semibold">xunohosteddata</span>.</li>
            <li>Run Backup Now queues a backup to run now.</li>
            <li>Execution: Background queue.</li>
            <li>Backup retention is managed on the DB Backup Retention page.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {/* Hidden instead of unmounted so JobTable keeps unsaved time/enabled edits on tab switch */}
      <div id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0} hidden={activeTab !== "operations"}>
      <div>
        <div className="workspace-card mx-auto mt-2 w-full overflow-hidden border border-slate-200 bg-white px-5 py-4">
          <h3 className="text-sm font-bold leading-5 text-slate-800">Database Backup Schedule</h3>
          <p className="mt-1 text-[11px] leading-4 text-slate-500">
            Set the daily backup time for each database server or run a backup now.
          </p>
          <JobTable data={jobs} onSave={updateJob} onRunNow={runBackupNow} />
        </div>
      </div>

      <ActivityLogTable
        title="Activity Log"
        description="Tracks schedule changes and backup runs."
        items={activityLogRows}
        loading={activityLogsLoading}
        page={activityLogsPage}
        totalPages={activityLogsTotalPages}
        totalCount={activityLogsTotalCount}
        onRefresh={() => fetchActivityLogs(activityLogsPage)}
        onPageChange={(pageNumber) => fetchActivityLogs(pageNumber)}
      />
      </div>
    </div>
  );
};

const JobTable = ({
  data,
  onSave,
  onRunNow,
}: {
  data: JobRow[];
  onSave: (jobId: string, scheduleTime: string, isEnabled: boolean) => Promise<void>;
  onRunNow: (jobId: string) => Promise<void>;
}) => {
  const [jobs, setJobs] = useState<JobRow[]>([]);

  useEffect(() => {
    if (!data?.length) {
      setJobs([]);
      return;
    }

    setJobs(data.map((row) => ({ ...row })));
  }, [data]);

  // ── Event Handlers: Update schedule time, enable/disable, and save changes ──
  const handleToggle = (index: number, checked: boolean) => {
    setJobs((prevRows) => {
      const newJobs = [...prevRows];
      newJobs[index] = { ...newJobs[index], isEnabled: checked };
      return newJobs;
    });
  };

  const handleTimeChange = (index: number, value: string) => {
    setJobs((prevRows) => {
      const newJobs = [...prevRows];
      newJobs[index] = { ...newJobs[index], scheduleTimeValue: value };
      return newJobs;
    });
  };

  const handleUpdateJob = async (index: number) => {
    if (index >= jobs.length) {
      return;
    }

    const job = jobs[index];
    await onSave(job.rowKey, job.scheduleTimeValue, job.isEnabled);
  };

  // ── Render: Table UI for schedule management ──────────────────────────────
  return (
    <div className="mt-3 overflow-x-auto rounded border border-slate-200 bg-white">
      <table className="min-w-[860px] w-full table-fixed divide-y divide-slate-200 text-xs">
        <colgroup>
          <col className="w-[30%]" />
          <col className="w-[15%]" />
          <col className="w-[20%]" />
          <col className="w-[12%]" />
          <col className="w-[23%]" />
        </colgroup>
        <thead className="bg-slate-50">
          <tr className="text-left text-[11px] text-slate-600">
            <th className="px-3 py-2.5 font-semibold">Database Server</th>
            <th className="px-3 py-2.5 font-semibold">Last Backup</th>
            <th className="px-3 py-2.5 font-semibold">Schedule ({getTimeZoneLabel()})</th>
            <th className="px-3 py-2.5 font-semibold">Status</th>
            <th className="px-3 py-2.5 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {jobs.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                No DB backup schedules found.
              </td>
            </tr>
          ) : (
            jobs.map((job, index) => (
              <tr key={job.rowKey} className="align-middle hover:bg-slate-50/70">
                <td className="px-3 py-2.5">
                  <p className="truncate font-medium text-slate-800" title={job.server}>{job.server}</p>
                  <p className="mt-0.5 truncate text-[10px] text-slate-500" title={`Server ID ${job.serverId}`}>
                    Server ID: {job.serverId}
                  </p>
                </td>
                <td className="px-3 py-2.5 text-slate-600">
                  {job.lastRun ? (
                    <div className="whitespace-pre-line leading-4" title={job.lastRun}>
                      {job.lastRun.replace(" ", "\n")}
                    </div>
                  ) : "Not run yet"}
                </td>
                <td className="px-3 py-2">
                  <div className="w-[120px]">
                    <input
                      type="time"
                      aria-label={`Schedule time for ${job.server}`}
                      value={job.scheduleTimeValue}
                      onChange={(e) => handleTimeChange(index, e.target.value)}
                      disabled={!job.isEnabled}
                      className="rounded-sm border border-gray-300 px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    />
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-start gap-2">
                    <input
                      type="checkbox"
                      aria-label={`Enable schedule for ${job.server}`}
                      checked={job.isEnabled}
                      onChange={(e) => handleToggle(index, e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className={`text-[11px] ${getStatusTextClass(job.isEnabled ? "enabled" : "disabled")}`}>
                      {job.isEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex w-[124px] flex-col gap-1.5">
                    <button
                      type="button"
                      className={`${btn.applySmall} w-full`}
                      onClick={() => {
                        void handleUpdateJob(index);
                      }}
                    >
                      Apply Schedule
                    </button>
                    <button
                      type="button"
                      className={`${btn.secondarySmall} w-full`}
                      onClick={() => {
                        void onRunNow(job.rowKey);
                      }}
                    >
                      Run Backup Now
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DbBackupSchedule;
